import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'

import App from './App'
import { ConfirmDialogProvider } from './components/feedback/ConfirmDialog'
import { queryClient } from './lib/queryClient'
import { AuthProvider } from './contexts/AuthContext'

// Architecture CSS 3 couches (cf CLAUDE.md > "CSS — Architecture 3 couches") :
// l'ordre d'import EST significatif. Les couches 1 (palette brute) et 2
// (vocabulaire d'usage `--color-*`) doivent être chargées AVANT globals.css
// (couche 3) qui les consomme via `var(--color-action-primary)`, etc.
// Sans ces deux imports, les `var()` ne résolvent pas → palette absente
// (fond blanc, boutons transparents).
import './styles/tokens/primitives.css'
import './styles/tokens/semantic.css'
import './styles/globals.css'
import './styles/app-extensions.css'

/**
 * Toast policy — INFO PASSIVE UNIQUEMENT.
 *
 * `toast.success()` / `toast.error()` / `toast.info()` sont autorises pour
 * confirmer une action deja effectuee, ou signaler une erreur transverse.
 * `toast.warning()` est autorise pour des avertissements passifs.
 *
 * INTERDICTION (verrouillee par ESLint `no-restricted-syntax`) : tout toast
 * avec `action: { ... }`. Une "action toast" est destructive : l'utilisateur
 * a 5s pour cliquer "Annuler", il rate, l'action est confirmee. Pour toute
 * mutation, utiliser `<Modal>` + `useConfirm()` qui force une confirmation
 * explicite, accessible (focus trap, ESC, ARIA).
 */

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('Root element #root not found in index.html')

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ConfirmDialogProvider>
            <App />
            <Toaster position="top-right" closeButton />
          </ConfirmDialogProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
